import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import type { SurgeryQuickKpiTotals } from '../types';

export interface SurgeryKpiSectionProps {
  totals: SurgeryQuickKpiTotals | null | undefined;
  loading?: boolean;
}

const MetricCard = ({
  labelId,
  value,
}: {
  labelId: string;
  value: string | number;
}) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      <Translate id={labelId} />
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const SurgeryKpiSection: React.FC<SurgeryKpiSectionProps> = ({ totals, loading }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.surgery.kpis.loading" />
        </Typography>
      </Box>
    );
  }

  if (!totals) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.no_data" />
        </Typography>
      </Box>
    );
  }

  const medianDisplay =
    totals.medianReadyToOperationDays > 0
      ? totals.medianReadyToOperationDays.toFixed(1)
      : '—';

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">
          <Translate id="hospital.analytics.surgery.kpis.section_title" />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.surgery.kpis.section_subtitle" />
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard labelId="hospital.analytics.surgery.kpis.planned" value={totals.planned} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard labelId="hospital.analytics.surgery.kpis.completed" value={totals.completed} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard labelId="hospital.analytics.surgery.kpis.cancelled" value={totals.cancelled} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            labelId="hospital.analytics.surgery.kpis.waiting"
            value={totals.currentlyWaitingPatients}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            labelId="hospital.analytics.surgery.kpis.median_delay"
            value={medianDisplay}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            labelId="hospital.analytics.surgery.kpis.patients_added"
            value={totals.patientsAdded}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurgeryKpiSection;
